import { Components, Theme } from '@mui/material/styles';
import { radii, brand } from './tokens';
import { ITorqTheme } from './torqTypes';

// Construído identicamente por Light.ts e Dark.ts — antes cada arquivo
// sobrescrevia um subconjunto diferente de componentes MUI (Light tinha
// AppBar/Paper/Card/OutlinedInput/Button/Divider/LinearProgress, Dark tinha
// AppBar/Card/Button/Table/TableCell), o que fazia um tema ficar "mais
// completo" que o outro sem ninguém perceber. Uma função só elimina essa
// divergência (guia seção 60).
export const buildComponents = (mode: 'light' | 'dark', torq: ITorqTheme): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        scrollbarWidth: 'thin',
        scrollbarColor: `${torq.scrollbarThumb} transparent`,
        '&::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: torq.scrollbarThumb,
          borderRadius: `${radii.sm}px`,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: torq.scrollbarThumbHover,
        },
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: torq.surface.card,
        color: torq.text.primary,
        boxShadow: 'none',
        borderBottom: `1px solid ${torq.border.default}`,
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: radii.sm,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        minHeight: 38,
      },
      outlined: {
        borderColor: torq.border.strong,
        '&:hover': { borderColor: torq.border.strong },
      },
      containedPrimary: {
        '&:hover': { backgroundColor: brand.primaryHover },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: radii.sm },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: torq.surface.card,
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: torq.surface.card,
        border: `1px solid ${torq.border.default}`,
        borderRadius: radii.lg,
        boxShadow: 'none',
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 16,
        '&:last-child': { paddingBottom: 16 },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radii.sm,
        backgroundColor: mode === 'dark' ? 'rgba(255,255,255,.015)' : '#FFFFFF',
        transition: 'border-color 150ms ease',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: mode === 'dark' ? 'rgba(148,163,184,.14)' : '#DDE4EC',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: mode === 'dark' ? 'rgba(148,163,184,.25)' : torq.border.strong,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: torq.border.focus,
          borderWidth: 1,
        },
      },
      input: {
        padding: '10px 14px',
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: torq.text.secondary,
        fontSize: '0.8125rem',
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      icon: { color: torq.text.secondary },
    },
  },

  MuiAutocomplete: {
    styleOverrides: {
      paper: {
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        borderRadius: radii.md,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radii.pill,
        fontWeight: 600,
      },
      outlined: {
        borderColor: torq.border.default,
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 40 },
      indicator: { backgroundColor: brand.primary },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        color: torq.text.secondary,
        minHeight: 40,
        '&.Mui-selected': { color: torq.text.primary },
      },
    },
  },

  MuiTable: {
    styleOverrides: {
      root: { backgroundColor: 'transparent' },
    },
  },

  // Padrão de listagem do sistema (guia TORQ seção 5): cabeçalho com fundo
  // levemente destacado do corpo da tabela (`surface.page`, a mesma cor que
  // já quebra o branco da página), texto em versalete, e cor de texto
  // "sem cor" — #002233 (neutro escuro) no tema claro, texto claro neutro
  // no escuro, nunca a cor de acento.
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: `1px solid ${torq.border.default}`,
        fontSize: '0.8125rem',
        color: torq.text.primary,
      },
      head: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: mode === 'dark' ? torq.text.primary : brand.menuBase,
        backgroundColor: torq.surface.page,
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': { backgroundColor: torq.hover },
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: { borderColor: torq.border.default },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'dark' ? 'rgba(32,205,247,0.16)' : 'rgba(32,205,247,0.12)',
        borderRadius: 4,
      },
      bar: { borderRadius: 4 },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: radii.xl,
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '1.125rem',
        fontWeight: 600,
        padding: '20px 24px',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '0 24px 20px' },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: { padding: '16px 24px', gap: 8 },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: radii.md,
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        minHeight: 38,
        fontSize: '0.875rem',
        borderRadius: radii.xs,
        margin: '2px 6px',
        width: 'auto',
        '&.Mui-selected': { backgroundColor: torq.selected },
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: radii.md,
        backgroundColor: torq.surface.elevated,
        border: `1px solid ${torq.border.default}`,
        boxShadow: torq.dialogShadow,
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#172033',
        color: '#F8FAFC',
        fontSize: '0.75rem',
        borderRadius: radii.xs,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: radii.md },
    },
  },

  MuiPaginationItem: {
    styleOverrides: {
      root: { borderRadius: radii.sm },
    },
  },
});
